import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const ACTION_LABELS = {
  campaign_approve: ["Approved campaign", "bg-emerald-100 text-emerald-800"],
  campaign_reject: ["Rejected campaign", "bg-red-100 text-red-700"],
  campaign_changes_requested: ["Requested changes", "bg-sky-100 text-sky-800"],
  payout_approve: ["Approved payout", "bg-emerald-100 text-emerald-800"],
  pledge_refund: ["Refunded pledge", "bg-amber-100 text-amber-800"],
  user_suspend: ["Suspended user", "bg-red-100 text-red-700"],
  user_reactivate: ["Reactivated user", "bg-emerald-100 text-emerald-800"],
  kyc_toggle: ["KYC toggled", "bg-saffron-100 text-saffron-700"],
  ticket_reply: ["Replied to ticket", "bg-slate-100 text-navy-700"],
  ticket_resolve: ["Resolved ticket", "bg-emerald-100 text-emerald-800"],
  settings_update: ["Updated settings", "bg-[#FFFBF5] text-[#E85D04] border border-[#E85D04]/40"],
};

const AdminAuditLog = () => {
  const { api } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/audit-log").then((r) => setRows(r.data)).finally(() => setLoading(false)); }, [api]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-audit-log-page">
      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E85D04]">Audit</span>
        <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">Audit log</h1>
        <p className="mt-1 text-sm text-[#0D1B2A]/60">Read-only record of every admin action across the platform.</p>
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-[#0D1B2A]/10 overflow-hidden">
        {loading ? <div className="p-12 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#0D1B2A]/60" /></div> :
          rows.length === 0 ? <div className="p-12 text-center text-[#0D1B2A]/60">No admin actions yet.</div> : (
          <ol className="divide-y divide-[#0D1B2A]/5">
            {rows.map((r) => {
              const [label, pill] = ACTION_LABELS[r.action_type] || [r.action_type, "bg-slate-100 text-navy-700"];
              return (
                <li key={r.action_id} className="px-5 py-4 hover:bg-[#FFFBF5]/60" data-testid={`audit-row-${r.action_id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`text-xs rounded-full px-2 py-0.5 ${pill}`}>{label}</span>
                    <span className="text-sm font-mono text-[#0D1B2A]/50">{r.target_id}</span>
                    <span className="ml-auto text-xs text-[#0D1B2A]/60">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 text-xs text-[#0D1B2A]/70 flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#E85D04]" /> {r.admin_email || r.admin_id}
                  </div>
                  {r.reason && <div className="mt-1 text-sm text-[#0D1B2A]">"{r.reason}"</div>}
                  {r.meta && Object.keys(r.meta).length > 0 && (
                    <div className="mt-1 text-xs font-mono text-[#0D1B2A]/50">{JSON.stringify(r.meta)}</div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLog;
